import type { BoardTile } from '../../model/board/BoardTypes';
import type { Logic } from '../Logic';
import type { BoardLogicContext } from './BoardLogicContext';

const HOP_DURATION_MS = 280;

export interface RollAndAdvanceResult {
  rolled: number;
  /** Tile the player landed on after consuming the full roll. */
  landed: BoardTile;
  /** Index of the landed tile. */
  landedIndex: number;
}

/**
 * Pulls a die roll, animates the player token tile-by-tile to the new
 * position, and credits any tile reward as it lands. Resolves when the
 * full hop sequence has finished.
 *
 * <p>Hops are animated one tile at a time so a roll of 4 reads as four
 * distinct steps — the cs-core MMS pattern. Each hop calls
 * {@link BoardModel#advance}{@code(1)}, which emits position-changed +
 * tile-activated for every intermediate tile (good for analytics, sound,
 * particle effects).
 */
export class RollAndAdvanceLogic implements Logic<void, RollAndAdvanceResult> {
  constructor(private readonly ctx: BoardLogicContext) {}

  async execute(): Promise<RollAndAdvanceResult> {
    const { model, widget, random } = this.ctx;
    const rolled = model.rollDie(random);

    let landed: BoardTile = model.currentTile;
    let landedIndex = model.position;
    for (let step = 0; step < rolled; step++) {
      landed = model.advance(1);
      landedIndex = model.position;
      widget.setHighlightedIndex(landedIndex);
      await widget.hopTokenTo(landedIndex, HOP_DURATION_MS);
    }
    return { rolled, landed, landedIndex };
  }
}
